import type { PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import type { Extent } from 'ol/extent';

import { assert, assertNonNullable } from '@disy/lib/custom-error';

import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { MapCouplingGroupDto, MapCouplingGroupId } from 'cadenza/workbook/model/map-coupling-group-dto';
import {
  getMapCouplingGroupWithState,
  getMapCouplingGroupWithStateById,
  getMostLeftTopViewOfGroup,
  getOverviewMainMapViewId,
  getOverviewMapContextByMainMapViewId,
  getZoomRelatedViewIds,
} from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { getViewMetadata } from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import type { ViewMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';

export interface MapViewMetadata extends ViewMetadata {
  mapExtent?: WorldBoxDto; // the map extent that is updated while interacting with the map (zooming, moving etc.) (see also MapViewContextDto.currentExtent)
  mapScale?: number;
  visibleRangeByLayerId?: Record<LayerId, boolean>;
}

export interface MapViewWorkbookState {
  /**
   * Holds configuration and transient state of map coupling groups (map views that try
   * to synchronize either scale or extent).
   * Coupled map views reference their group id.
   */
  mapCouplingGroupsById: Record<MapCouplingGroupId, MapCouplingGroupWithState>;
  zoomRelatedViewIds?: ViewId[];
  /**
   * Used for communication between the main map view and the overview map view.
   * It should keep the current state of the view, synchronized with UI view changes like e.g. zooming (in contrary to view.mapViewContext.currentExtent which is set from the backend).
   */
  overviewMapContextsByMainMapViewId: Record<ViewId, OverviewMapContext>;
}

export interface OverviewMapContext {
  currentExtent: Extent;
  initial?: boolean; //if the value was set in the context during map initialization
}

/**
 * Extends map coupling group by a transient state of current target scale/extent.
 */
export interface MapCouplingGroupWithState extends MapCouplingGroupDto {
  /** Target extent for coupled views */
  extent?: Extent;
  /** Target scale for coupled views */
  scale?: number;
  /** View that set the new target values. Used to avoid feedback loops of changed loop */
  initiator?: ViewId;
  animatingOrInteracting?: boolean;
  isLayerLimitExceeded: boolean;
}

export function createInitialMapViewState() {
  return {
    mapCouplingGroupsById: {},
    overviewMapContextsByMainMapViewId: {},
  };
}

export const mapViewReducers = {
  setTargetMapExtentAndScaleForCoupledView(
    state: RootStateOr<WorkbookState>,
    {
      payload: { viewId, extent, scale, animatingOrInteracting = false, isLayerLimitExceeded = false },
    }: PayloadAction<{
      viewId: ViewId;
      extent: WorldBoxDto;
      scale: number;
      animatingOrInteracting?: boolean;
      isLayerLimitExceeded?: boolean;
    }>,
  ) {
    const group = getMapCouplingGroupWithState(state, viewId);
    assert(group, 'Map view not part of coupling group');

    Object.assign(group, {
      scale,
      extent,
      initiator: viewId,
      animatingOrInteracting,
      isLayerLimitExceeded,
    });
  },
  stopInteractionForCoupledView(
    state: RootStateOr<WorkbookState>,
    { payload: { viewId } }: PayloadAction<{ viewId: ViewId }>,
  ) {
    const group = getMapCouplingGroupWithState(state, viewId);
    assert(group, 'Map view not part of coupling group');

    group.animatingOrInteracting = false;
  },
  synchronizeToLeftTopMostView(
    state: RootStateOr<WorkbookState>,
    { payload: { groupId } }: PayloadAction<{ groupId: MapCouplingGroupId }>,
  ) {
    const mostLeftTopView = getMostLeftTopViewOfGroup(state, groupId);
    const metadata = getViewMetadata<MapViewMetadata>(state, mostLeftTopView.id)!;
    const group = getMapCouplingGroupWithStateById(state, groupId);

    Object.assign(group, {
      initiator: mostLeftTopView.id,
      scale: metadata.mapScale!,
      extent: metadata.mapExtent!,
      isLayerLimitExceeded: false,
    });
  },
  setZoomRelatedViewId(state, { payload }: PayloadAction<ViewId[]>) {
    state.zoomRelatedViewIds = [...payload];
  },
  removeZoomRelatedViewId(state, { payload }: PayloadAction<ViewId>) {
    state.zoomRelatedViewIds = getZoomRelatedViewIds(state).filter((id) => id !== payload);
  },
  setMainMapContextForOverviewMap(
    state: RootStateOr<WorkbookState>,
    { payload }: PayloadAction<{ mainMapViewId: ViewId; extent: Extent; initial?: boolean }>,
  ) {
    const { mainMapViewId, extent, initial } = payload;
    const context = getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId);
    slice(state).overviewMapContextsByMainMapViewId[mainMapViewId] = Object.assign(context ?? {}, {
      currentExtent: extent,
      initial,
    });
  },
  // Takes viewId of overview map and extent of overview map and sets that extent on corresponding main map (used on overview map interaction)
  setMainMapExtentForOverviewMapViewId(
    state: RootStateOr<WorkbookState>,
    { payload }: PayloadAction<{ overviewMapViewId: ViewId; extent: Extent }>,
  ) {
    const { overviewMapViewId, extent } = payload;
    const mainMapViewId = getOverviewMainMapViewId(state, overviewMapViewId);
    assertNonNullable(mainMapViewId, `${overviewMapViewId} is not a valid ID of an overview map view`);
    Object.assign(getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId) ?? {}, {
      currentExtent: extent,
      initial: false,
    });
  },
} satisfies SliceCaseReducers<WorkbookState>;
